/**
 * Pure mapping from raw leg rows + their parent order onto the leg shape the
 * confirm plan consumes.
 *
 * This lives outside the hook so the multi-leg rules below are unit-testable —
 * every leg-1-only regression found so far (a later leg inheriting leg 1's
 * truck / trailer, or a later leg falsely reported as missing a trip type)
 * came from this mapping being inline and untested.
 *
 * The rules, in one place:
 *  - Equipment (trailer number, trailer type) on the parent `orders` row is a
 *    back-compat mirror of leg 1 ONLY. Legs 2..N must never inherit it.
 *  - Trip type is NOT equipment. "Same trip type for all legs" mode never
 *    writes a per-leg value, so a leg with no trip type of its own genuinely
 *    runs the order's — the fallback applies to every leg.
 *  - Tractor is authoritative per leg, no parent fallback at all.
 */

export interface ConfirmPlanLegSource {
  id: string;
  leg_sequence: number;
  assigned_tractor_id: string | null;
  assigned_tractor?: { name?: string | null } | null;
  assigned_operator_id?: string | null;
  trailer_id?: string | null;
  trailer_type_id?: string | null;
  trip_type?: string | null;
  eapi_trip_id?: string | null;
  driver_status?: string | null;
  eapi_status?: string | null;
}

export interface ConfirmPlanOrderSource {
  status?: string | null;
  trailer_id?: string | null;
  trailer_type_id?: string | null;
  trip_type?: string | null;
  assigned_operator_id?: string | null;
}


/**
 * True when the parent order's mirrored equipment columns describe this leg.
 * Single-leg orders keep the mirror (the parent row *is* the leg); multi-leg
 * orders only mirror leg 1.
 */
export function legMirrorsParentEquipment(
  legSequence: number,
  legCount: number,
): boolean {
  return legCount === 1 || legSequence === 1;
}

export interface MappedConfirmPlanLeg {
  id: string;
  legSequence: number;
  status: string | null;
  assignedTractorId: string | null;
  tractorLabel: string | null;
  assignedOperatorId: string | null;
  trailerId: string | null;
  trailerTypeId: string | null;
  tripType: string | null;
  eapiTripId: string | null;
}

export function mapConfirmPlanLegs(
  legs: readonly ConfirmPlanLegSource[],
  order: ConfirmPlanOrderSource | null | undefined,
  resolveStatus: (leg: ConfirmPlanLegSource, isMultiLeg: boolean) => string | null,
): MappedConfirmPlanLeg[] {
  const count = legs.length;
  return legs.map((leg) => {
    const mirrorsParent = legMirrorsParentEquipment(leg.leg_sequence, count);
    return {
      id: leg.id,
      legSequence: leg.leg_sequence,
      status: resolveStatus(leg, count > 1),
      assignedTractorId: leg.assigned_tractor_id ?? null,
      tractorLabel: leg.assigned_tractor?.name ?? null,
      // Operators are assigned per leg; the parent column mirrors leg 1 only.
      assignedOperatorId:
        leg.assigned_operator_id ??
        (mirrorsParent ? order?.assigned_operator_id ?? null : null),
      trailerId: leg.trailer_id ?? (mirrorsParent ? order?.trailer_id ?? null : null),
      trailerTypeId:
        leg.trailer_type_id ?? (mirrorsParent ? order?.trailer_type_id ?? null : null),
      tripType: leg.trip_type ?? order?.trip_type ?? null,
      eapiTripId: leg.eapi_trip_id ?? null,
    };
  });
}

